/**
 * Reset Password Page
 *
 * Landing page for the Supabase password-recovery email link.
 * Flow:
 *   1. User clicks reset link in email
 *   2. Supabase verifies the recovery token and establishes a recovery session
 *   3. Browser is redirected here (`/reset-password`)
 *   4. Component detects the recovery session, lets the user set a new password,
 *      and on success sends them to the home page (which will be authenticated).
 *
 * If the user lands here without a valid recovery session (link expired,
 * bookmarked URL, etc.) we show a friendly error with a link back to home.
 */

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import LockResetIcon from '@mui/icons-material/LockReset';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import './ResetPasswordPage.css';

const MIN_LENGTH = 8;

const ResetPasswordPage: React.FC = () => {
  const navigate = useNavigate();
  const { updatePassword } = useAuth();
  const [checking, setChecking] = useState(true);
  const [sessionValid, setSessionValid] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  // On mount, verify a recovery session exists. Supabase places the user in
  // a recovery session after the email-link redirect; if there's no session
  // the link is expired or invalid.
  useEffect(() => {
    let cancelled = false;
    const checkSession = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!cancelled) {
        setSessionValid(!!session);
        setChecking(false);
      }
    };
    checkSession();

    // Also listen for the PASSWORD_RECOVERY event in case the session is
    // established slightly after mount (race between component and Supabase).
    const { data: subscription } = supabase.auth.onAuthStateChange((event, session) => {
      if (cancelled) return;
      if (event === 'PASSWORD_RECOVERY' || (event === 'SIGNED_IN' && session)) {
        setSessionValid(true);
        setChecking(false);
      }
    });

    return () => {
      cancelled = true;
      subscription.subscription.unsubscribe();
    };
  }, []);

  const handleSubmit = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    setError(null);

    if (newPassword.length < MIN_LENGTH) {
      setError(`Password must be at least ${MIN_LENGTH} characters`);
      return;
    }
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setLoading(true);
    try {
      const { error: updateError } = await updatePassword(newPassword);
      if (updateError) {
        setError(updateError.message || 'Could not update password');
      } else {
        setSuccess(true);
      }
    } catch (err: any) {
      setError(err?.message || 'Unexpected error updating password');
    } finally {
      setLoading(false);
    }
  };

  if (checking) {
    return (
      <div className="reset-password-page">
        <div className="reset-password-card">
          <p className="reset-password-loading">Verifying reset link...</p>
        </div>
      </div>
    );
  }

  if (!sessionValid) {
    return (
      <div className="reset-password-page">
        <div className="reset-password-card">
          <ErrorOutlineIcon className="reset-password-icon reset-password-icon--error" />
          <h1>Reset link expired</h1>
          <p>
            This password reset link is no longer valid. Request a new one from the sign-in
            screen and try again.
          </p>
          <button className="reset-password-btn" onClick={() => navigate('/')}>
            Back to sign in
          </button>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="reset-password-page">
        <div className="reset-password-card">
          <CheckCircleIcon className="reset-password-icon reset-password-icon--success" />
          <h1>Password updated</h1>
          <p>You're all set. Use your new password the next time you sign in.</p>
          <button className="reset-password-btn" onClick={() => navigate('/')}>
            Go to Knods
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="reset-password-page">
      <form className="reset-password-card" onSubmit={handleSubmit}>
        <LockResetIcon className="reset-password-icon" />
        <h1>Set a new password</h1>
        <p className="reset-password-subtitle">Choose a new password for your Knods account.</p>

        <div className="reset-password-field">
          <label htmlFor="rp-new">New password</label>
          <input
            id="rp-new"
            type={showPassword ? 'text' : 'password'}
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            disabled={loading}
            autoFocus
            autoComplete="new-password"
          />
        </div>

        <div className="reset-password-field">
          <label htmlFor="rp-confirm">Confirm new password</label>
          <input
            id="rp-confirm"
            type={showPassword ? 'text' : 'password'}
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            disabled={loading}
            autoComplete="new-password"
          />
        </div>

        <label className="reset-password-show-toggle">
          <input
            type="checkbox"
            checked={showPassword}
            onChange={(e) => setShowPassword(e.target.checked)}
            disabled={loading}
          />
          Show password
        </label>

        {error && <div className="reset-password-error">{error}</div>}

        <button
          type="submit"
          className="reset-password-btn"
          disabled={loading || !newPassword || !confirmPassword}
        >
          {loading ? 'Updating...' : 'Update password'}
        </button>
      </form>
    </div>
  );
};

export default ResetPasswordPage;
